import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Button, IconButton,
  CircularProgress, Tooltip, Chip, Divider, Dialog,
  DialogTitle, DialogContent, DialogActions, Collapse,
  Select, MenuItem, FormControl, TextField, LinearProgress, Badge,
} from '@mui/material';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase/config';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import AddIcon           from '@mui/icons-material/Add';
import UploadFileIcon    from '@mui/icons-material/UploadFile';
import OpenInNewIcon     from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon  from '@mui/icons-material/PictureAsPdf';
import HtmlIcon          from '@mui/icons-material/Html';
import CachedIcon        from '@mui/icons-material/Cached';
import RateReviewIcon    from '@mui/icons-material/RateReview';
import ArrowBackIcon     from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PresentToAllIcon  from '@mui/icons-material/PresentToAll';
import LinkIcon          from '@mui/icons-material/Link';
import EditIcon          from '@mui/icons-material/Edit';
import QrCodeIcon        from '@mui/icons-material/QrCode';
import ImageIcon         from '@mui/icons-material/Image';
import ExpandMoreIcon    from '@mui/icons-material/ExpandMore';
import ExpandLessIcon    from '@mui/icons-material/ExpandLess';
import AutoAwesomeIcon   from '@mui/icons-material/AutoAwesome';
import SlideshowIcon     from '@mui/icons-material/Slideshow';
import TvIcon            from '@mui/icons-material/Tv';
import UnfoldMoreIcon    from '@mui/icons-material/UnfoldMore';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import CheckIcon        from '@mui/icons-material/Check';
import CloseIcon        from '@mui/icons-material/Close';
import ContentCopyIcon  from '@mui/icons-material/ContentCopy';
import EmailIcon        from '@mui/icons-material/Email';
import BarChartIcon     from '@mui/icons-material/BarChart';
import DownloadIcon     from '@mui/icons-material/Download';
import DevicesIcon      from '@mui/icons-material/Devices';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import LabelIcon        from '@mui/icons-material/Label';
import Menu             from '@mui/material/Menu';
import { collection as fbCollection, getDocs as fbGetDocs } from 'firebase/firestore';
import { useAuth }       from '../../contexts/AuthContext';
import { useAltLogo } from '../../hooks/useQRLogo';
import LagbasLogo        from '../../components/LagbasLogo';
import SlideRenderer     from './slides/SlideRenderer';
import { listPitches, deletePitch, updatePitch, loadBackgrounds, duplicatePitch, getViews, createExternalPitch, getNewCommentCounts } from './services/pitchService';
import { colors, fontFamily } from '../../theme/tikabTokens';
import PitchMailDialog from './PitchMailDialog';
import QRDialog from '../shared/sharing/QRDialog';

const accent = colors.orange;

const PITCH_TYPE = {
  system:      { label: 'PITCH',  bg: '#1565C0', color: '#fff', rowBg: 'rgba(21,101,192,0.04)'  },
  html:        { label: 'HTML',   bg: '#00695C', color: '#fff', rowBg: 'rgba(0,105,92,0.05)'    },
  pdf:         { label: 'PDF',    bg: '#B71C1C', color: '#fff', rowBg: 'rgba(183,28,28,0.04)'   },
  applab_app:  { label: 'APP',    bg: '#00897B', color: '#fff', rowBg: 'rgba(0,137,123,0.04)'   },
};

const AUTHORING_TOOLS = ['Claude.ai', 'PitchN', 'Canva', 'Google Slides', 'PowerPoint'];

function pitchTypeStyle(pitch) {
  if (pitch.pitchType === 'html')       return PITCH_TYPE.html;
  if (pitch.pitchType === 'pdf')        return PITCH_TYPE.pdf;
  if (pitch.pitchType === 'applab_app') return PITCH_TYPE.applab_app;
  return PITCH_TYPE.system;
}

// ── Upload-dialog för externa presentationer ──────────────────────────────────

function UploadExternalDialog({ open, onClose, userId, onUploaded }) {
  const [file,          setFile]          = useState(null);
  const [title,         setTitle]         = useState('');
  const [client,        setClient]        = useState('');
  const [authoringTool, setAuthoringTool] = useState('');
  const [customTool,    setCustomTool]    = useState('');
  const [progress,      setProgress]      = useState(0);
  const [busy,          setBusy]          = useState(false);
  const [error,         setError]         = useState('');
  const inputRef = useRef();

  function reset() {
    setFile(null); setTitle(''); setClient('');
    setAuthoringTool(''); setCustomTool('');
    setProgress(0); setBusy(false); setError('');
  }

  function handleClose() { if (!busy) { reset(); onClose(); } }

  function handleFileChange(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^/.]+$/, ''));
  }

  async function handleUpload() {
    if (!file || !title.trim()) return;
    setBusy(true);
    setError('');
    try {
      const ext      = file.name.split('.').pop().toLowerCase();
      const pitchType = ext === 'pdf' ? 'pdf' : 'html';
      const path     = `pitches/external/${userId}/${crypto.randomUUID()}.${ext}`;
      const sRef     = storageRef(storage, path);
      const task     = uploadBytesResumable(sRef, file);

      await new Promise((resolve, reject) => {
        task.on('state_changed',
          snap => setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
          reject,
          resolve,
        );
      });

      const url = await getDownloadURL(sRef);
      const resolvedTool = authoringTool === '__custom__' ? customTool.trim() : authoringTool;
      const { id } = await createExternalPitch({
        title:               title.trim(),
        client:              client.trim(),
        createdBy:           userId,
        pitchType,
        externalFileUrl:     url,
        externalStoragePath: path,
        authoringTool:       resolvedTool || '',
      });

      onUploaded(id);
      reset();
      onClose();
    } catch (e) {
      console.error('Upload failed:', e);
      setError('Uppladdningen misslyckades. Försök igen.');
      setBusy(false);
    }
  }

  const isHtml = file?.name.toLowerCase().endsWith('.html') || file?.name.toLowerCase().endsWith('.htm');
  const isPdf  = file?.name.toLowerCase().endsWith('.pdf');
  const validFile = isHtml || isPdf;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <UploadFileIcon sx={{ color: accent }} />
        Ladda upp presentation
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>

        {/* Filväljare */}
        <Box
          onClick={() => !busy && inputRef.current?.click()}
          sx={{
            border: '2px dashed',
            borderColor: file && validFile ? accent : 'divider',
            borderRadius: 2, p: 3,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
            cursor: busy ? 'default' : 'pointer',
            bgcolor: file && validFile ? accent + '08' : 'transparent',
            transition: 'border-color 0.2s, background 0.2s',
            '&:hover': { borderColor: busy ? undefined : accent + 'aa' },
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".html,.htm,.pdf"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          {file ? (
            <>
              {isPdf
                ? <PictureAsPdfIcon sx={{ fontSize: 36, color: PITCH_TYPE.pdf.bg }} />
                : <HtmlIcon         sx={{ fontSize: 36, color: PITCH_TYPE.html.bg }} />
              }
              <Typography fontWeight={700} textAlign="center" noWrap sx={{ maxWidth: 240 }}>{file.name}</Typography>
              <Chip
                label={isPdf ? 'PDF' : 'HTML'}
                size="small"
                sx={{ bgcolor: isPdf ? PITCH_TYPE.pdf.bg : PITCH_TYPE.html.bg, color: '#fff', fontWeight: 700, fontSize: '0.68rem' }}
              />
            </>
          ) : (
            <>
              <UploadFileIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Klicka för att välja fil
              </Typography>
              <Typography variant="caption" color="text.disabled">.html / .pdf</Typography>
            </>
          )}
        </Box>

        {file && !validFile && (
          <Typography variant="caption" color="error">Endast .html och .pdf stöds.</Typography>
        )}

        <TextField
          label="Titel"
          size="small"
          value={title}
          onChange={e => setTitle(e.target.value)}
          disabled={busy}
          fullWidth
        />
        <TextField
          label="Kund (valfritt)"
          size="small"
          value={client}
          onChange={e => setClient(e.target.value)}
          disabled={busy}
          fullWidth
        />

        {/* Verktyg */}
        <Box>
          <Typography variant="caption" fontWeight={700} color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
            Skapat med
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {AUTHORING_TOOLS.map(tool => (
              <Chip
                key={tool}
                label={tool}
                size="small"
                clickable
                disabled={busy}
                onClick={() => setAuthoringTool(t => t === tool ? '' : tool)}
                sx={{
                  fontWeight: authoringTool === tool ? 700 : 400,
                  bgcolor: authoringTool === tool ? accent + '22' : 'transparent',
                  color: authoringTool === tool ? accent : 'text.secondary',
                  border: '1px solid',
                  borderColor: authoringTool === tool ? accent : 'divider',
                  transition: 'all 0.15s',
                }}
              />
            ))}
            <Chip
              label="Annat…"
              size="small"
              clickable
              disabled={busy}
              onClick={() => setAuthoringTool(t => t === '__custom__' ? '' : '__custom__')}
              sx={{
                fontWeight: authoringTool === '__custom__' ? 700 : 400,
                bgcolor: authoringTool === '__custom__' ? accent + '22' : 'transparent',
                color: authoringTool === '__custom__' ? accent : 'text.secondary',
                border: '1px solid',
                borderColor: authoringTool === '__custom__' ? accent : 'divider',
                transition: 'all 0.15s',
              }}
            />
          </Box>
          {authoringTool === '__custom__' && (
            <TextField
              size="small" autoFocus fullWidth
              placeholder="Ange verktyg…"
              value={customTool}
              onChange={e => setCustomTool(e.target.value)}
              disabled={busy}
              sx={{ mt: 1 }}
            />
          )}
        </Box>

        {busy && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 1, height: 6 }} />
            <Typography variant="caption" color="text.secondary" textAlign="center">{progress}%</Typography>
          </Box>
        )}

        {error && <Typography variant="caption" color="error">{error}</Typography>}

      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={busy}>Avbryt</Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={busy || !file || !validFile || !title.trim()}
          sx={{ bgcolor: accent, '&:hover': { bgcolor: '#c44d00' }, textTransform: 'none' }}
        >
          {busy ? 'Laddar upp…' : 'Ladda upp'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Ersätt-fil-dialog för externa presentationer ─────────────────────────────

function ReplaceFileDialog({ open, pitch, onClose, onReplaced }) {
  const [file,     setFile]     = useState(null);
  const [progress, setProgress] = useState(0);
  const [busy,     setBusy]     = useState(false);
  const [error,    setError]    = useState('');
  const inputRef = useRef();

  function reset() { setFile(null); setProgress(0); setBusy(false); setError(''); }
  function handleClose() { if (!busy) { reset(); onClose(); } }

  function handleFileChange(e) {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  }

  async function handleReplace() {
    if (!file || !pitch) return;
    setBusy(true); setError('');
    try {
      const ext  = file.name.split('.').pop().toLowerCase();
      const path = `pitches/external/${pitch.createdBy}/${crypto.randomUUID()}.${ext}`;
      const sRef = storageRef(storage, path);
      const task = uploadBytesResumable(sRef, file);

      await new Promise((resolve, reject) => {
        task.on('state_changed',
          snap => setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
          reject, resolve,
        );
      });

      const url = await getDownloadURL(sRef);
      await onReplaced(pitch.id, { externalFileUrl: url, externalStoragePath: path });
      reset(); onClose();
    } catch (e) {
      console.error('Replace failed:', e);
      setError('Uppladdningen misslyckades. Försök igen.');
      setBusy(false);
    }
  }

  const isPdf = file?.name.toLowerCase().endsWith('.pdf');
  const validFile = file && (
    file.name.toLowerCase().endsWith('.html') ||
    file.name.toLowerCase().endsWith('.htm') ||
    file.name.toLowerCase().endsWith('.pdf')
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <CachedIcon sx={{ color: accent }} />
        Ersätt presentation
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
        <Typography variant="body2" color="text.secondary">
          Laddar upp ny version av <strong>{pitch?.title}</strong>. Den gamla filen ersätts.
        </Typography>

        <Box
          onClick={() => !busy && inputRef.current?.click()}
          sx={{
            border: '2px dashed',
            borderColor: file && validFile ? accent : 'divider',
            borderRadius: 2, p: 3,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
            cursor: busy ? 'default' : 'pointer',
            bgcolor: file && validFile ? accent + '08' : 'transparent',
            '&:hover': { borderColor: busy ? undefined : accent + 'aa' },
          }}
        >
          <input ref={inputRef} type="file" accept=".html,.htm,.pdf"
            style={{ display: 'none' }} onChange={handleFileChange} />
          {file ? (
            <>
              {isPdf
                ? <PictureAsPdfIcon sx={{ fontSize: 32, color: PITCH_TYPE.pdf.bg }} />
                : <HtmlIcon         sx={{ fontSize: 32, color: PITCH_TYPE.html.bg }} />
              }
              <Typography fontWeight={700} textAlign="center" noWrap sx={{ maxWidth: 240 }}>{file.name}</Typography>
            </>
          ) : (
            <>
              <UploadFileIcon sx={{ fontSize: 32, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary">Välj ny fil (.html / .pdf)</Typography>
            </>
          )}
        </Box>

        {file && !validFile && (
          <Typography variant="caption" color="error">Endast .html och .pdf stöds.</Typography>
        )}

        {busy && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 1, height: 6 }} />
            <Typography variant="caption" color="text.secondary" textAlign="center">{progress}%</Typography>
          </Box>
        )}

        {error && <Typography variant="caption" color="error">{error}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={busy}>Avbryt</Button>
        <Button
          variant="contained"
          onClick={handleReplace}
          disabled={busy || !file || !validFile}
          sx={{ bgcolor: accent, '&:hover': { bgcolor: '#c44d00' }, textTransform: 'none' }}
        >
          {busy ? 'Laddar upp…' : 'Ersätt'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Instruktionspanel ─────────────────────────────────────────────────────────

function Step({ number, title, children }) {
  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
      <Box sx={{
        width: 26, height: 26, borderRadius: '50%', bgcolor: accent, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 0.3,
      }}>
        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.78rem' }}>{number}</Typography>
      </Box>
      <Box>
        <Typography fontWeight={700} mb={0.3}>{title}</Typography>
        <Typography color="text.secondary" fontSize="0.85rem" lineHeight={1.6}>{children}</Typography>
      </Box>
    </Box>
  );
}

function InstructionsPanel({ navigate }) {
  const [open, setOpen] = useState(false);
  return (
    <Paper variant="outlined" sx={{
      borderRadius: 2, mb: 3, overflow: 'hidden',
      borderColor: open ? accent + '66' : accent + '33',
      background: open
        ? `linear-gradient(135deg, ${accent}0a 0%, transparent 60%)`
        : `linear-gradient(135deg, ${accent}08 0%, transparent 50%)`,
      transition: 'border-color 0.2s, background 0.3s',
    }}>
      <Box onClick={() => setOpen(o => !o)} sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2,
        cursor: 'pointer', userSelect: 'none',
      }}>
        <AutoAwesomeIcon sx={{ color: accent, fontSize: 20 }} />
        <Typography fontWeight={700} sx={{ flex: 1 }}>
          Vad är Pitch<span style={{ color: accent }}>N</span>?
        </Typography>
        {open ? <ExpandLessIcon sx={{ color: 'text.secondary' }} /> : <ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 3, pb: 3 }}>
          <Divider sx={{ mb: 2.5 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 3 }}>

            {/* Kolumn 1 — Skapa */}
            <Box>
              <Typography variant="caption" fontWeight={700} color="text.disabled"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1.5 }}>
                Skapa
              </Typography>
              <Step number="1" title="AI-generera från fritext">
                Klistra in brief, mötesanteckningar eller bakgrundsbeskrivning. AI:n bygger en komplett presentation direkt.
              </Step>
              <Step number="2" title="Ladda upp extern presentation">
                Har du en HTML- eller PDF-presentation skapad utanför systemet? Ladda upp den via <em>Ladda upp</em>-knappen — välj verktyg (t.ex. Claude.ai) och den syns i listan med sin egen typbadge.
              </Step>
              <Step number="3" title="Finjustera slides">
                Sortera om slides med drag-och-släpp, redigera text, byt bakgrundsbild eller lägg till egna slides.
              </Step>
            </Box>

            {/* Kolumn 2 — Presentera & dela */}
            <Box>
              <Typography variant="caption" fontWeight={700} color="text.disabled"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1.5 }}>
                Presentera & dela
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {[
                  { icon: <SlideshowIcon sx={{ fontSize: 18, color: accent }} />, text: 'Presentera i helskärmsläge med talarnoter och timer' },
                  { icon: <TvIcon sx={{ fontSize: 18, color: accent }} />, text: 'Skjut ut till HDMI-skärm — styr varifrån du vill' },
                  { icon: <LinkIcon sx={{ fontSize: 18, color: accent }} />, text: 'Delningslänk och QR-kod — kunden öppnar presentationen i sin webbläsare' },
                  { icon: <BarChartIcon sx={{ fontSize: 18, color: accent }} />, text: 'Statistik per presentation: visningar, visningstid, slide-räckvidd, enhetssplit' },
                ].map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    {item.icon}
                    <Typography fontSize="0.85rem" color="text.secondary" lineHeight={1.5}>{item.text}</Typography>
                  </Box>
                ))}
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/pitch/new')}
                sx={{ bgcolor: accent, '&:hover': { bgcolor: '#c44d00' }, fontFamily, textTransform: 'none', mt: 2 }}
              >
                Skapa din första pitch
              </Button>
            </Box>

            {/* Kolumn 3 — Granska */}
            <Box>
              <Typography variant="caption" fontWeight={700} color="text.disabled"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1.5 }}>
                Granska
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                {[
                  { icon: <RateReviewIcon sx={{ fontSize: 18, color: '#D63384' }} />, text: 'Klicka på granskningsikonen på en presentation för att öppna granskvyn' },
                  { icon: <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: '#D63384', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Typography sx={{ color: '#fff', fontSize: '0.6rem', fontWeight: 700 }}>1</Typography></Box>, text: 'Aktivera granskningsläge och klicka var som helst i presentationen för att placera en plupp' },
                  { icon: <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: '#388E3C', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Typography sx={{ color: '#fff', fontSize: '0.6rem', fontWeight: 700 }}>✓</Typography></Box>, text: 'Varje plupp får en BEAst-status: Ny kommentar → Ska åtgärdas → Har åtgärdats / Avvisad' },
                  { icon: <AutoAwesomeIcon sx={{ fontSize: 18, color: 'text.disabled' }} />, text: 'AI-redigering av HTML-presentationer — planerad framtida funktion' },
                ].map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    {item.icon}
                    <Typography fontSize="0.85rem" color="text.secondary" lineHeight={1.5}>{item.text}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
}

// ── QR-dialog — wrapper kring shared/sharing/QRDialog ────────────────────────

function PitchQRDialog({ pitch, open, onClose }) {
  if (!pitch) return null;
  return (
    <QRDialog
      open={open}
      onClose={onClose}
      url={`${window.location.origin}/p/${pitch.shareToken}`}
      title={pitch.title || 'Namnlös pitch'}
    />
  );
}

// ── Statistik-dialog ──────────────────────────────────────────────────────────

function fmt(sec) {
  if (!sec || sec < 1) return '–';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function StatsDialog({ pitch, open, onClose }) {
  const [views,   setViews]   = useState([]);
  const [loading, setLoading] = useState(false);
  const loaded = useRef(false);

  useEffect(() => {
    if (!open || !pitch?.id || loaded.current) return;
    setLoading(true);
    getViews(pitch.id)
      .then(v => { setViews(v); loaded.current = true; })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, pitch?.id]);

  // Reset when pitch changes
  useEffect(() => { loaded.current = false; setViews([]); }, [pitch?.id]);

  if (!pitch) return null;

  const total      = views.length;
  const totalSlides = pitch.slides?.filter(s => !s.hidden).length || 1;

  // Duration: diff between startedAt and lastSeen (seconds)
  const durations = views
    .map(v => {
      const a = v.startedAt?.toDate?.();
      const b = v.lastSeen?.toDate?.();
      if (!a || !b) return null;
      return (b - a) / 1000;
    })
    .filter(d => d !== null && d > 2);

  const avgDuration = durations.length ? durations.reduce((s, d) => s + d, 0) / durations.length : null;
  const completed   = views.filter(v => v.completed).length;
  const completePct = total ? Math.round((completed / total) * 100) : 0;

  // Views last 7 days
  const now = Date.now();
  const week7 = views.filter(v => {
    const d = v.startedAt?.toDate?.();
    return d && (now - d.getTime()) < 7 * 24 * 3600 * 1000;
  }).length;

  // Funnel: for each slide index, how many viewers reached it
  const funnelCounts = Array.from({ length: totalSlides }, (_, i) =>
    views.filter(v => v.slidesReached >= i).length
  );

  // Device split
  const mobile  = views.filter(v => v.device === 'mobil').length;
  const desktop = total - mobile;

  // Recent 10 sessions
  const recent = views.slice(0, 10);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BarChartIcon sx={{ color: accent }} />
          <Box>
            <Typography fontWeight={700}>{pitch.title || 'Namnlös pitch'}</Typography>
            <Typography variant="caption" color="text.secondary">Visningsstatistik</Typography>
          </Box>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* ── Sammanfattning ── */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 2 }}>
              {[
                { label: 'Totalt visningar', value: total },
                { label: 'Snitt visningstid', value: fmt(avgDuration) },
                { label: 'Slutförda', value: `${completePct}%` },
                { label: 'Senaste 7 dagarna', value: week7 },
              ].map(({ label, value }) => (
                <Paper key={label} variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                  <Typography variant="h5" fontWeight={700} sx={{ color: accent }}>{value}</Typography>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                </Paper>
              ))}
            </Box>

            {total === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                Inga visningar registrerade ännu. Dela länken för att börja samla statistik.
              </Typography>
            ) : (<>

              {/* ── Enhetssplit ── */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PhoneAndroidIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                <Typography variant="body2" color="text.secondary">
                  Mobil: <strong>{mobile}</strong>
                </Typography>
                <DevicesIcon sx={{ color: 'text.secondary', fontSize: 18, ml: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Dator: <strong>{desktop}</strong>
                </Typography>
              </Box>

              {/* ── Slide-funnel ── */}
              <Box>
                <Typography variant="caption" fontWeight={700} color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1.5 }}>
                  Räckvidd per slide
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {funnelCounts.map((count, i) => {
                    const pct = total ? Math.round((count / total) * 100) : 0;
                    const slide = pitch.slides?.filter(s => !s.hidden)?.[i];
                    const label = slide?.title || slide?.type || `Slide ${i + 1}`;
                    return (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant="caption" sx={{ width: 22, color: 'text.disabled', flexShrink: 0 }}>{i + 1}</Typography>
                        <Typography variant="caption" noWrap sx={{ width: 160, flexShrink: 0 }}>{label}</Typography>
                        <Box sx={{ flex: 1, height: 8, bgcolor: '#f0f0f0', borderRadius: 1, overflow: 'hidden' }}>
                          <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: pct > 60 ? accent : pct > 30 ? '#ff9800' : '#bbb', borderRadius: 1, transition: 'width 0.4s' }} />
                        </Box>
                        <Typography variant="caption" sx={{ width: 40, textAlign: 'right', color: 'text.secondary', flexShrink: 0 }}>{pct}%</Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* ── Senaste sessioner ── */}
              <Box>
                <Typography variant="caption" fontWeight={700} color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
                  Senaste visningar
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {recent.map(v => {
                    const d = v.startedAt?.toDate?.();
                    const dur = (() => {
                      const a = v.startedAt?.toDate?.();
                      const b = v.lastSeen?.toDate?.();
                      return (a && b) ? (b - a) / 1000 : null;
                    })();
                    return (
                      <Box key={v.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.75,
                        borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" sx={{ width: 120, color: 'text.secondary', flexShrink: 0 }}>
                          {d ? d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '–'}
                        </Typography>
                        <Typography variant="caption" sx={{ width: 50, flexShrink: 0 }}>{v.device || '–'}</Typography>
                        <Typography variant="caption" sx={{ width: 60, flexShrink: 0, color: 'text.secondary' }}>{fmt(dur)}</Typography>
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {Array.from({ length: totalSlides }, (_, i) => (
                            <Box key={i} sx={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                              bgcolor: i <= v.slidesReached ? accent : '#e0e0e0' }} />
                          ))}
                        </Box>
                        {v.completed && <Chip label="Klar" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: '0.65rem', height: 18 }} />}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </>)}

          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Stäng</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Rad i listan ──────────────────────────────────────────────────────────────

// canEdit: ägaren eller medredaktör (collaborators). isOwner: får även radera.
// Speglar reglerna för pitches i firestore.rules.
function PitchRow({ pitch, backgrounds, altLogoUrl, ao, navigate, onDelete, onDuplicate, duplicating, onCopy, copied, onQR, onMail, onRename, onStats, products, onSetProduct, onReplace, newCommentCount, canEdit, isOwner }) {
  const firstSlide = pitch.slides?.[0];
  const [renaming,     setRenaming]     = useState(false);
  const [draftTitle,   setDraftTitle]   = useState('');
  const [productAnchor, setProductAnchor] = useState(null);
  const assignedProduct = products?.find(p => p.id === pitch.productId);

  function startRename(e) {
    e.stopPropagation();
    setDraftTitle(pitch.title || '');
    setRenaming(true);
  }
  async function confirmRename(e) {
    e.stopPropagation();
    if (draftTitle.trim()) await onRename(pitch.id, draftTitle.trim());
    setRenaming(false);
  }
  function cancelRename(e) {
    e.stopPropagation();
    setRenaming(false);
  }

  const typeStyle = pitchTypeStyle(pitch);

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 2,
      px: 2, py: 1.5,
      borderBottom: '1px solid',
      borderColor: 'divider',
      bgcolor: typeStyle.rowBg,
      transition: 'background 0.1s',
      '&:hover': { filter: 'brightness(0.97)' },
      cursor: renaming ? 'default' : 'pointer',
    }}
    onClick={() => {
      if (renaming) return;
      if (pitch.pitchType === 'html' || pitch.pitchType === 'pdf') {
        navigate(`/pitch/${pitch.id}/view`);
      } else {
        navigate(`/pitch/${pitch.id}/edit`);
      }
    }}
    >
      {/* Miniatyr */}
      <Box sx={{ width: 96, flexShrink: 0, borderRadius: 1, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.1)' }}>
        {pitch.pitchType === 'html' ? (
          /* Live mini-render — hela HTML-sidan skalad till 16:9-thumbnail */
          <Box sx={{ width: 96, height: 54, overflow: 'hidden', position: 'relative', bgcolor: '#fff', flexShrink: 0 }}>
            <iframe
              src={pitch.externalFileUrl}
              title={pitch.title || 'Presentation preview'}
              tabIndex={-1}
              loading="lazy"
              style={{
                width: '960px',
                height: '540px',
                border: 'none',
                display: 'block',
                pointerEvents: 'none',
                userSelect: 'none',
                transformOrigin: 'top left',
                transform: 'scale(0.1)',
              }}
            />
          </Box>
        ) : pitch.pitchType === 'pdf' ? (
          <Box sx={{ width: 96, height: 54, display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: '#B71C1C18' }}>
            <PictureAsPdfIcon sx={{ fontSize: 28, color: PITCH_TYPE.pdf.bg }} />
          </Box>
        ) : firstSlide ? (
          <SlideRenderer slide={firstSlide} backgrounds={backgrounds} slideTheme={pitch.slideTheme} altLogoUrl={altLogoUrl} />
        ) : (
          <Box sx={{ width: 96, height: 54, bgcolor: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SlideshowIcon sx={{ color: 'rgba(255,255,255,0.2)', fontSize: 28 }} />
          </Box>
        )}
      </Box>

      {/* Titel & kund */}
      <Box sx={{ flex: 1, minWidth: 0 }} onClick={e => renaming && e.stopPropagation()}>
        {renaming ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TextField
              size="small" autoFocus
              value={draftTitle}
              onChange={e => setDraftTitle(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') confirmRename(e); if (e.key === 'Escape') cancelRename(e); }}
              sx={{ flex: 1 }}
              inputProps={{ style: { fontWeight: 700 } }}
            />
            <IconButton size="small" onClick={confirmRename} sx={{ color: 'success.main' }}><CheckIcon fontSize="small" /></IconButton>
            <IconButton size="small" onClick={cancelRename}><CloseIcon fontSize="small" /></IconButton>
          </Box>
        ) : (
          <>
            <Typography fontWeight={700} noWrap>{pitch.title || 'Namnlös pitch'}</Typography>
            {pitch.client && (
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {pitch.client}
              </Typography>
            )}
            {pitch.authoringTool && (
              <Typography variant="caption" noWrap display="block"
                sx={{ color: 'text.disabled', fontSize: '0.68rem' }}>
                {pitch.authoringTool}
              </Typography>
            )}
          </>
        )}
      </Box>

      {/* Typ-badge */}
      <Chip
        label={typeStyle.label}
        size="small"
        sx={{ bgcolor: typeStyle.bg, color: typeStyle.color, fontWeight: 700, fontSize: '0.68rem', fontFamily, flexShrink: 0 }}
      />

      {/* AO-chip (superadmin) */}
      {ao && (
        <Chip label={ao} size="small" variant="outlined"
          sx={{ flexShrink: 0, minWidth: 52, fontWeight: 700, fontSize: '0.72rem' }} />
      )}

      {/* Produkt-chip */}
      {products?.length > 0 && (
        <>
          <Chip
            icon={<LabelIcon sx={{ fontSize: '12px !important' }} />}
            label={assignedProduct ? assignedProduct.name : '+ Produkt'}
            size="small"
            variant={assignedProduct ? 'filled' : 'outlined'}
            onClick={canEdit ? e => { e.stopPropagation(); setProductAnchor(e.currentTarget); } : undefined}
            sx={{
              flexShrink: 0, fontSize: '0.68rem', cursor: 'pointer',
              bgcolor: assignedProduct ? '#FF6B0022' : 'transparent',
              color: assignedProduct ? accent : 'text.disabled',
              borderColor: assignedProduct ? accent + '66' : undefined,
              '&:hover': { bgcolor: accent + '18' },
            }}
          />
          <Menu anchorEl={productAnchor} open={Boolean(productAnchor)}
            onClose={() => setProductAnchor(null)}>
            <MenuItem dense onClick={() => { onSetProduct(pitch.id, ''); setProductAnchor(null); }}>
              <Typography variant="body2" color="text.disabled"><em>Ingen produkt</em></Typography>
            </MenuItem>
            {products.map(p => (
              <MenuItem key={p.id} dense selected={p.id === pitch.productId}
                onClick={() => { onSetProduct(pitch.id, p.id); setProductAnchor(null); }}>
                <Typography variant="body2">{p.name}</Typography>
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      {/* Slides-chip (bara för systemtyp) */}
      {!pitch.pitchType && (
        <Chip
          label={`${pitch.slides?.length || 0} slides`}
          size="small"
          variant="outlined"
          sx={{ flexShrink: 0 }}
        />
      )}

      {/* Åtgärder */}
      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
        {/* System-pitch: redigera + presentera + QR + statistik + dela + duplicera */}
        {!pitch.pitchType && (<>
          <Tooltip title="Redigera">
            <IconButton size="small" onClick={() => navigate(`/pitch/${pitch.id}/edit`)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canEdit && (
            <Tooltip title="Byt namn">
              <IconButton size="small" onClick={startRename}>
                <DriveFileRenameOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Presentera">
            <IconButton size="small" onClick={() => navigate(`/pitch/${pitch.id}/present`)}>
              <PresentToAllIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="QR-kod">
            <IconButton size="small" onClick={() => onQR(pitch)}>
              <QrCodeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Dela via mail">
            <IconButton size="small" onClick={() => onMail(pitch)} sx={{ '&:hover': { color: '#1565C0' } }}>
              <EmailIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Statistik">
            <IconButton size="small" onClick={() => onStats(pitch)}>
              <BarChartIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={copied === pitch.id ? 'Kopierat!' : 'Kopiera delningslänk'}>
            <IconButton
              size="small"
              onClick={() => onCopy(pitch)}
              sx={{ color: copied === pitch.id ? 'success.main' : undefined }}
            >
              <LinkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Duplicera">
            <IconButton
              size="small"
              disabled={duplicating === pitch.id}
              onClick={() => onDuplicate(pitch.id)}
              sx={{ '&:hover': { color: accent } }}
            >
              {duplicating === pitch.id
                ? <CircularProgress size={14} />
                : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </>)}

        {/* Extern pitch (HTML/PDF): öppna + byt namn + QR + statistik + dela + ersätt */}
        {(pitch.pitchType === 'html' || pitch.pitchType === 'pdf') && (<>
          <Tooltip title="Öppna presentation">
            <IconButton size="small" onClick={() => navigate(`/pitch/${pitch.id}/view`)}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {pitch.pitchType === 'html' && (
            <Tooltip title="Ladda ner HTML">
              <IconButton
                size="small"
                onClick={async () => {
                  try {
                    const res = await fetch(pitch.externalFileUrl);
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${pitch.title || 'presentation'}.html`;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch {
                    window.open(pitch.externalFileUrl, '_blank');
                  }
                }}
                sx={{ '&:hover': { color: '#1565C0' } }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canEdit && (
            <Tooltip title="Byt namn">
              <IconButton size="small" onClick={startRename}>
                <DriveFileRenameOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="QR-kod">
            <IconButton size="small" onClick={() => onQR(pitch)}>
              <QrCodeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Dela via mail">
            <IconButton size="small" onClick={() => onMail(pitch)} sx={{ '&:hover': { color: '#1565C0' } }}>
              <EmailIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Statistik">
            <IconButton size="small" onClick={() => onStats(pitch)}>
              <BarChartIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={copied === pitch.id ? 'Kopierat!' : 'Kopiera delningslänk'}>
            <IconButton
              size="small"
              onClick={() => onCopy(pitch)}
              sx={{ color: copied === pitch.id ? 'success.main' : undefined }}
            >
              <LinkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canEdit && (
            <Tooltip title="Ersätt fil">
              <IconButton size="small" onClick={() => onReplace(pitch)} sx={{ '&:hover': { color: accent } }}>
                <CachedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </>)}

        <Tooltip title={newCommentCount > 0 ? `Granska (${newCommentCount} nya kommentarer)` : 'Granska'}>
          <IconButton size="small" onClick={() => navigate(`/pitch/${pitch.id}/review`)}
            sx={{ '&:hover': { color: '#D63384' } }}>
            <Badge
              badgeContent={newCommentCount || null}
              sx={{
                '& .MuiBadge-badge': {
                  bgcolor: '#D63384', color: '#fff',
                  fontSize: '0.6rem', fontWeight: 700,
                  minWidth: 16, height: 16, borderRadius: 8,
                },
              }}
            >
              <RateReviewIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>
        {isOwner && (
          <Tooltip title="Radera">
            <IconButton
              size="small"
              sx={{ color: '#bbb', '&:hover': { color: '#d32f2f' } }}
              onClick={() => onDelete(pitch.id)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

// ── Huvud ─────────────────────────────────────────────────────────────────────

export default function PitchList() {
  const navigate                = useNavigate();
  const { currentUser }  = useAuth();
  const altLogoUrl              = useAltLogo();
  const [pitches,     setPitches]     = useState([]);
  const [backgrounds, setBackgrounds] = useState([]);
  const [departments, setDepartments] = useState([]); // { id, name, offerCode }
  const [loading,     setLoading]     = useState(true);
  const [loadError,   setLoadError]   = useState('');
  const [deleteId,    setDeleteId]    = useState(null);
  const [deleting,    setDeleting]    = useState(false);
  const [duplicating, setDuplicating] = useState('');
  const [copied,      setCopied]      = useState('');
  const [qrPitch,     setQrPitch]     = useState(null);
  const [statsPitch,  setStatsPitch]  = useState(null);
  const [mailPitch,   setMailPitch]   = useState(null);
  const [sortField,   setSortField]   = useState('createdAt'); // 'title' | 'ao' | 'createdAt'
  const [sortAsc,     setSortAsc]     = useState(false);
  const [aoFilter,    setAoFilter]    = useState('');
  const [products,    setProducts]    = useState([]);
  const [productFilter, setProductFilter] = useState('');
  const [uploadOpen,        setUploadOpen]        = useState(false);
  const [replacePitch,      setReplacePitch]      = useState(null);
  const [newCommentCounts,  setNewCommentCounts]  = useState({});

  // Sort/filter pitches
  const displayedPitches = useMemo(() => {
    let list = [...pitches];
    if (aoFilter) list = list.filter(p => p.departmentId === aoFilter);
    if (productFilter) list = list.filter(p => p.productId === productFilter);
    list.sort((a, b) => {
      let av = '', bv = '';
      if (sortField === 'title') { av = a.title || ''; bv = b.title || ''; }
      else if (sortField === 'ao') {
        const da = departments.find(d => d.id === a.departmentId);
        const db2 = departments.find(d => d.id === b.departmentId);
        av = da?.offerCode || da?.name || '';
        bv = db2?.offerCode || db2?.name || '';
      } else {
        // createdAt desc by default
        return sortAsc
          ? (a.createdAt?.seconds ?? 0) - (b.createdAt?.seconds ?? 0)
          : (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0);
      }
      return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return list;
  }, [pitches, sortField, sortAsc, aoFilter, departments]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleSort(field) {
    if (sortField === field) setSortAsc(a => !a);
    else { setSortField(field); setSortAsc(true); }
  }

  const load = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setLoadError('');
    try {
      const tasks = [
        listPitches(currentUser.uid, { all: true }),
        loadBackgrounds(),
        getDocs(collection(db, 'departments')),
        fbGetDocs(fbCollection(db, 'products')),
        getNewCommentCounts(),
      ];
      const results = await Promise.all(tasks);
      setPitches(results[0]);
      setBackgrounds(results[1]);
      setDepartments(results[2].docs.map(d => ({ id: d.id, ...d.data() })));
      setProducts(results[3].docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      setNewCommentCounts(results[4]);
    } catch (e) {
      console.error('PitchList load error:', e);
      setLoadError(e.message || 'Kunde inte hämta presentationer.');
    } finally {
      setLoading(false);
    }
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  async function handleDelete() {
    if (!deleteId) return;
    setDeleting(true);
    await deletePitch(deleteId);
    setPitches(prev => prev.filter(p => p.id !== deleteId));
    setDeleteId(null);
    setDeleting(false);
  }

  async function handleDuplicate(pitchId) {
    setDuplicating(pitchId);
    try {
      const { id: newId } = await duplicatePitch(pitchId, currentUser.uid);
      // Reload list so the copy appears at top
      const fresh = await listPitches(currentUser.uid, { all: true });
      setPitches(fresh);
      navigate(`/pitch/${newId}/edit`);
    } catch (e) {
      console.error('Duplicate failed:', e);
    } finally {
      setDuplicating('');
    }
  }

  async function handleRename(pitchId, newTitle) {
    await updatePitch(pitchId, { title: newTitle });
    setPitches(prev => prev.map(p => p.id === pitchId ? { ...p, title: newTitle } : p));
  }

  async function handleReplace(pitchId, { externalFileUrl, externalStoragePath }) {
    await updatePitch(pitchId, { externalFileUrl, externalStoragePath });
    setPitches(prev => prev.map(p =>
      p.id === pitchId ? { ...p, externalFileUrl, externalStoragePath } : p
    ));
  }

  async function handleSetProduct(pitchId, productId) {
    await updatePitch(pitchId, { productId: productId || '' });
    setPitches(prev => prev.map(p => p.id === pitchId ? { ...p, productId: productId || '' } : p));
  }

  function copyShareLink(pitch) {
    const url = `${window.location.origin}/p/${pitch.shareToken}`;
    navigator.clipboard.writeText(url);
    setCopied(pitch.id);
    setTimeout(() => setCopied(''), 2000);
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* ── Header ────────────────────────────────────────────────────────── */}
      <Box sx={{
        bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', gap: 2,
      }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
        <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Pitch<span style={{ color: accent }}>N</span>
          </Typography>
          <Typography variant="caption" sx={{ color: '#aaa' }}>
            AI-drivna kundpresentationer
          </Typography>
        </Box>
        <Tooltip title="Hantera bakgrundsbilder">
          <IconButton
            size="small"
            sx={{ color: '#aaa', '&:hover': { color: 'white' } }}
            onClick={() => navigate('/pitch/backgrounds')}
          >
            <ImageIcon />
          </IconButton>
        </Tooltip>
        <Button
          variant="outlined"
          startIcon={<UploadFileIcon />}
          onClick={() => setUploadOpen(true)}
          sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.08)' }, fontFamily, textTransform: 'none' }}
        >
          Ladda upp
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/pitch/new')}
          sx={{ bgcolor: accent, '&:hover': { bgcolor: '#c44d00' }, fontFamily }}
        >
          Ny pitch
        </Button>
      </Box>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* ── Instruktioner ─────────────────────────────────────────────── */}
        <InstructionsPanel navigate={navigate} />

        {/* ── Lista ─────────────────────────────────────────────────────── */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : loadError ? (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <Typography color="error" gutterBottom>{loadError}</Typography>
            <Button onClick={load} variant="outlined" size="small" sx={{ mt: 1 }}>Försök igen</Button>
          </Paper>
        ) : pitches.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
            <SlideshowIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Inga pitchar ännu
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Skapa din första AI-genererade kundpresentation
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/pitch/new')}
              sx={{ bgcolor: accent, '&:hover': { bgcolor: '#c44d00' } }}
            >
              Skapa pitch
            </Button>
          </Paper>
        ) : (
          <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
            {/* Typ-legend */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 2,
              px: 2, py: 1,
              bgcolor: 'action.hover',
              borderBottom: '1px solid', borderColor: 'divider',
            }}>
              <Typography variant="caption" color="text.disabled" sx={{ mr: 0.5 }}>Typ:</Typography>
              {Object.values(PITCH_TYPE).map(t => (
                <Box key={t.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: t.bg, flexShrink: 0 }} />
                  <Typography variant="caption" color="text.secondary">{t.label}</Typography>
                </Box>
              ))}
            </Box>
            {/* Kolumnrubriker + filter */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 2,
              px: 2, py: 1,
              bgcolor: 'action.hover',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}>
              <Box sx={{ width: 96, flexShrink: 0 }} />
              {/* Sorterbara rubriker */}
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => toggleSort('title')}>
                <Typography variant="caption" fontWeight={700} color="text.secondary">PRESENTATION</Typography>
                {sortField === 'title' && <UnfoldMoreIcon sx={{ fontSize: 14, color: 'text.secondary', opacity: 0.6 }} />}
              </Box>
              {departments.length > 0 && (<>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', flexShrink: 0 }}
                  onClick={() => toggleSort('ao')}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary">AO</Typography>
                  {sortField === 'ao' && <UnfoldMoreIcon sx={{ fontSize: 14, color: 'text.secondary', opacity: 0.6 }} />}
                </Box>
                <FormControl size="small" sx={{ minWidth: 100, flexShrink: 0 }}>
                  <Select value={aoFilter} onChange={e => setAoFilter(e.target.value)}
                    displayEmpty sx={{ fontSize: '0.75rem', height: 28 }}>
                    <MenuItem value=""><em>Alla AO</em></MenuItem>
                    {departments.map(d => (
                      <MenuItem key={d.id} value={d.id}>{d.offerCode || d.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>)}
              {products.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 120, flexShrink: 0 }}>
                  <Select value={productFilter} onChange={e => setProductFilter(e.target.value)}
                    displayEmpty sx={{ fontSize: '0.75rem', height: 28 }}>
                    <MenuItem value=""><em>Alla produkter</em></MenuItem>
                    {products.map(p => (
                      <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <Box sx={{ width: 80, flexShrink: 0 }} />
              <Box sx={{ width: 140, flexShrink: 0 }} />
            </Box>
            {displayedPitches.map(pitch => {
              const dept = departments.find(d => d.id === pitch.departmentId);
              const ao   = dept?.offerCode || dept?.name || '';
              const isOwner = pitch.createdBy === currentUser?.uid;
              const canEdit = isOwner || (pitch.collaborators || []).includes(currentUser?.uid);
              return (
                <PitchRow
                  key={pitch.id}
                  pitch={pitch}
                  backgrounds={backgrounds}
                  altLogoUrl={altLogoUrl}
                  ao={ao}
                  navigate={navigate}
                  onDelete={setDeleteId}
                  onDuplicate={handleDuplicate}
                  duplicating={duplicating}
                  onCopy={copyShareLink}
                  copied={copied}
                  onQR={setQrPitch}
                  onMail={setMailPitch}
                  onStats={setStatsPitch}
                  onRename={handleRename}
                  onReplace={setReplacePitch}
                  products={products}
                  onSetProduct={handleSetProduct}
                  newCommentCount={newCommentCounts[pitch.id] || 0}
                  canEdit={canEdit}
                  isOwner={isOwner}
                />
              );
            })}
          </Paper>
        )}
      </Container>

      {/* ── Ersätt-fil-dialog ─────────────────────────────────────────────── */}
      <ReplaceFileDialog
        open={!!replacePitch}
        pitch={replacePitch}
        onClose={() => setReplacePitch(null)}
        onReplaced={handleReplace}
      />

      {/* ── Upload-dialog ─────────────────────────────────────────────────── */}
      <UploadExternalDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        userId={currentUser?.uid}
        onUploaded={async () => {
          const fresh = await listPitches(currentUser.uid, { all: true });
          setPitches(fresh);
        }}
      />

      {/* ── QR-dialog ─────────────────────────────────────────────────────── */}
      <PitchQRDialog
        pitch={qrPitch}
        open={!!qrPitch}
        onClose={() => setQrPitch(null)}
      />

      {/* ── Mail-dialog ───────────────────────────────────────────────────── */}
      <PitchMailDialog
        pitch={mailPitch}
        open={!!mailPitch}
        onClose={() => setMailPitch(null)}
        backgrounds={backgrounds}
        currentUser={currentUser}
      />

      {/* ── Statistik-dialog ──────────────────────────────────────────────── */}
      <StatsDialog
        pitch={statsPitch}
        open={!!statsPitch}
        onClose={() => setStatsPitch(null)}
      />

      {/* ── Radera-dialog ─────────────────────────────────────────────────── */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Radera pitch?</DialogTitle>
        <DialogContent>
          <Typography>Presentationen raderas permanent och kan inte återställas.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Avbryt</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={16} color="inherit" /> : 'Radera'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
